import React, { useEffect, useState } from 'react';
import { Quest } from '../types';

interface PenaltyWarningModalProps {
  isOpen: boolean;
  failedQuests: Quest[];
  onClose: () => void;
  onAcceptPenalty: () => void;
}

const PenaltyWarningModal: React.FC<PenaltyWarningModalProps> = ({
  isOpen,
  failedQuests,
  onClose,
  onAcceptPenalty,
}) => {
  const [showFlash, setShowFlash] = useState(false);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Flash effect
      setShowFlash(true);
      setTimeout(() => setShowFlash(false), 300);
      
      // Show content after flash
      setTimeout(() => setShowContent(true), 350);
    } else {
      setShowContent(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Flash Effect */}
      {showFlash && (
        <div className="fixed inset-0 bg-red-600 z-[9999] animate-fade-out pointer-events-none" />
      )}

      {/* Modal Overlay */}
      <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[9998] flex items-center justify-center p-2 sm:p-4">
        {/* Warning Particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-red-500 rounded-full animate-float-particle"
              style={{
                left: `${Math.random() * 100}%`,
                bottom: '-10px',
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`,
              }}
            />
          ))}
        </div>

        {/* Modal Content */}
        {showContent && (
          <div className="relative max-w-lg w-full max-h-[95vh] overflow-y-auto">
            {/* Glowing Background Orbs */}
            <div className="absolute inset-0 -z-10">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-red-600/20 rounded-full blur-3xl animate-glow-pulse" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-orange-600/20 rounded-full blur-3xl animate-glow-pulse" style={{ animationDelay: '0.5s' }} />
            </div>

            {/* Main Modal Card */}
            <div className="bg-gradient-to-b from-red-950/95 to-black/95 border-2 border-red-500/50 rounded-lg shadow-[0_0_30px_rgba(239,68,68,0.4)] overflow-hidden">
              {/* Hexagonal Pattern Overlay */}
              <div 
                className="absolute inset-0 opacity-5 pointer-events-none"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0l25.98 15v30L30 60 4.02 45V15z' fill='none' stroke='%23fff' stroke-width='1'/%3E%3C/svg%3E")`,
                  backgroundSize: '60px 60px',
                }}
              />

              {/* Top Border Glow */}
              <div className="h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent animate-pulse" />

              {/* Content */}
              <div className="relative p-3 sm:p-6">
                {/* Alert Icon */}
                <div className="flex justify-center mb-3 sm:mb-4">
                  <div className="relative">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-red-500/20 flex items-center justify-center animate-glow-pulse">
                      <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-red-500/30 flex items-center justify-center">
                        <span className="text-2xl sm:text-3xl">⚠️</span>
                      </div>
                    </div>
                    {/* Rotating ring */}
                    <div className="absolute inset-0 border-2 border-red-500/30 rounded-full animate-spin-slow" style={{ animationDuration: '8s' }} />
                  </div>
                </div>

                {/* Warning Title */}
                <div className="text-center mb-3 sm:mb-4">
                  <h2 className="text-xl sm:text-3xl font-orbitron font-black text-red-500 mb-0.5 sm:mb-1 tracking-wider drop-shadow-[0_0_10px_rgba(239,68,68,0.8)] uppercase">
                    ⚠️ WARNING ⚠️
                  </h2>
                  <h3 className="text-sm sm:text-lg font-orbitron font-bold text-red-400 uppercase tracking-wide">
                    Quest Failure Detected
                  </h3>
                </div>

                {/* System Message */}
                <div className="bg-black/50 border border-red-500/30 rounded-lg p-2 sm:p-4 mb-3 sm:mb-4">
                  <div className="font-rajdhani text-gray-300 text-center">
                    <p className="text-sm sm:text-sm">
                      <span className="text-red-400 font-bold">THE SYSTEM</span> has detected overdue quest(s):
                    </p>
                  </div>
                  
                  {/* Failed Quests List */}
                  <div className="mt-2 sm:mt-3 space-y-1.5 sm:space-y-2">
                    {failedQuests.map((quest) => {
                      // Determine penalty reason
                      let reason = '';
                      if (quest.resetDaily) {
                        reason = 'Missed Daily';
                      } else if (quest.scheduledFor) {
                        reason = 'Past Due Date';
                      } else {
                        reason = 'Not Completed';
                      }
                      
                      return (
                        <div 
                          key={quest.id}
                          className="bg-red-950/30 border border-red-500/30 rounded p-2 sm:p-2 flex items-center gap-2 sm:gap-2"
                        >
                          <span className="text-red-400 text-lg sm:text-lg">✕</span>
                          <div className="flex-1">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                              <div className="font-orbitron font-bold text-white text-xs sm:text-xs">{quest.title}</div>
                              <span className="text-xs sm:text-[10px] px-2 sm:px-1.5 py-1 sm:py-0.5 bg-red-500/20 text-red-400 rounded font-rajdhani font-bold whitespace-nowrap self-start sm:self-center">
                                {reason}
                              </span>
                            </div>
                            {quest.description && (
                              <div className="text-xs sm:text-xs text-gray-400 font-rajdhani mt-1 sm:mt-0.5 line-clamp-2 sm:line-clamp-1">{quest.description}</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Penalty Details */}
                <div className="bg-gradient-to-r from-red-950/50 via-red-900/50 to-red-950/50 border border-red-500/50 rounded-lg p-2 sm:p-4 mb-3 sm:mb-4">
                  <h4 className="text-xs sm:text-base font-orbitron font-bold text-red-400 uppercase mb-2 sm:mb-2 text-center">
                    💀 PENALTY QUEST ACTIVATED 💀
                  </h4>
                  <div className="text-center font-rajdhani text-gray-300">
                    <p className="text-sm sm:text-sm">
                      You will receive <span className="text-red-400 font-bold">ZERO XP</span> from completed quests until you complete the overdue quest(s).
                    </p>
                    <p className="text-xs sm:text-xs text-gray-400 mt-2 sm:mt-2">
                      Complete the overdue quest(s) to remove this penalty and restore normal XP gains.
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 sm:gap-4">
                  <button
                    onClick={onAcceptPenalty}
                    className="flex-1 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-orbitron font-bold py-3 sm:py-3 px-4 sm:px-6 rounded uppercase text-sm sm:text-base tracking-wider transition-all shadow-[0_0_20px_rgba(239,68,68,0.4)] hover:shadow-[0_0_30px_rgba(239,68,68,0.6)] border border-red-400/50 min-h-[44px]"
                  >
                    Accept Penalty
                  </button>
                </div>

                {/* System Quote */}
                <div className="mt-3 sm:mt-4 text-center">
                  <p className="text-xs sm:text-xs font-rajdhani text-gray-500 italic">
                    "Those who fail to prepare... prepare to fail."
                  </p>
                </div>
              </div>

              {/* Bottom Border Glow */}
              <div className="h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent animate-pulse" />
            </div>

            {/* Pulsing Outer Glow */}
            <div className="absolute inset-0 border-2 border-red-500/30 rounded-lg animate-pulse pointer-events-none" />
          </div>
        )}
      </div>
    </>
  );
};

export default PenaltyWarningModal;

